/**
 * The fixed signer's queue: every envelope in one batch that is waiting on
 * *this* person, oldest first.
 *
 * A counter-signer on a 500-row batch otherwise receives 500 separate signing
 * emails. "Waiting" is exact — a signer at order 2 of an ordered envelope is
 * excluded until order 1 has signed — so this list is only ever work that can
 * be done right now.
 *
 * **Known gap:** the queue returns `envelopeId` but no `requestToken`, and
 * opening the signing surface needs the token (see `handleSignNow`). Until
 * backend adds it, a row opens the request itself, from where the document can
 * be signed — one extra click per envelope rather than a straight run.
 */

import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, Skeleton, Typography } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";
import { useHistory, useParams } from "react-router-dom";
import EmptyState from "global/components/EmptyState/EmptyState";
import { centeredPageGutterSx } from "layouts/CenteredAuthenticated/CenteredPage.tokens";
import title from "global/constants/TitleConstants";
import viewpaths from "global/constants/ViewPathConstants";
import { useTitle } from "utils/UseTitle";
import { calculateRelativeTime, handleErrorMessage } from "helpers/methods";
import { batchStyles } from "./BulkSend.styles";
import tokens from "./BulkSend.tokens";
import { BulkBatch, BulkSigningQueueEntry } from "./BulkSendTypesAndValidation";
import { fetchBulkBatch, fetchBulkSigningQueue } from "./BulkSendService";

const p = tokens.palette;
const t = tokens.batch;
const s = batchStyles;

const subjectOf = (entry: BulkSigningQueueEntry): string =>
  entry.emailSubject || "Untitled document";

const createdOf = (entry: BulkSigningQueueEntry): string | undefined =>
  entry.createdOn;

const BulkSigningQueue = () => {
  useTitle(title.BULK_QUEUE);
  const history = useHistory();
  const { id } = useParams<{ id: string }>();

  const [batch, setBatch] = useState<BulkBatch | null>(null);
  const [entries, setEntries] = useState<BulkSigningQueueEntry[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasFailed, setHasFailed] = useState<boolean>(false);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setIsLoading(true);
      setHasFailed(false);
      const [batchResponse, queueResponse] = await Promise.all([
        fetchBulkBatch(id),
        fetchBulkSigningQueue(id),
      ]);
      setBatch(batchResponse);
      setEntries(queueResponse);
    } catch (error: any) {
      setHasFailed(true);
      handleErrorMessage(error);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const openEnvelope = (entry: BulkSigningQueueEntry) =>
    history.push(`${viewpaths.viewRequestViewPath}/${entry.envelopeId}`);

  if (isLoading) {
    return (
      <Box sx={centeredPageGutterSx}>
        <Box sx={s.card}>
          <Box sx={s.header}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Skeleton
                variant="text"
                sx={[s.title, { width: "38%" }] as SxProps<Theme>}
              />
              <Skeleton
                variant="text"
                sx={[s.sub, { width: "26%" }] as SxProps<Theme>}
              />
            </Box>
          </Box>
          {[0, 1, 2, 3].map((key) => (
            <Box key={key} sx={s.failedRow}>
              <Skeleton variant="circular" sx={s.failedAvatar} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Skeleton
                  variant="text"
                  sx={[s.failedName, { width: "44%" }] as SxProps<Theme>}
                />
                <Skeleton
                  variant="text"
                  sx={[s.failedContact, { width: "22%" }] as SxProps<Theme>}
                />
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  if (hasFailed) {
    return (
      <Box sx={centeredPageGutterSx}>
        <EmptyState
          size="full"
          icon="activity"
          title="Couldn't load your queue"
          body="The request didn't come back. Try again in a moment."
          primaryAction={{ label: "Try again", onClick: load }}
        />
      </Box>
    );
  }

  return (
    <Box sx={centeredPageGutterSx}>
      <Box sx={s.card}>
        <Box sx={s.header}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography component="span" sx={s.title}>
              {batch?.name || "Your queue"}
            </Typography>
            <Typography component="span" sx={s.sub}>
              You countersign each copy. Same fields every time.
            </Typography>
          </Box>
          {entries.length > 0 && (
            <Box sx={s.statusPill("progress")}>
              {entries.length} waiting for you
            </Box>
          )}
        </Box>

        {entries.length === 0 ? (
          <Box sx={{ p: t.tableRowPadding }}>
            <EmptyState
              size="medium"
              icon="activity"
              title="Nothing waiting for you"
              body="When a recipient in this batch signs, their copy appears here for your signature."
              primaryAction={{
                label: "Back to the batch",
                onClick: () =>
                  history.push(`${viewpaths.bulkBatchViewPath}/${id}`),
              }}
            />
          </Box>
        ) : (
          <>
            <Box sx={s.footer}>
              <Box component="span" sx={s.footerNote}>
                Oldest first — start at the top and work down.
              </Box>
              <Button
                variant="contained"
                onClick={() => openEnvelope(entries[0])}
                sx={s.actionButton}
              >
                Open the first
              </Button>
            </Box>

            {entries.map((entry, index) => (
              <Box key={entry.envelopeId} sx={s.failedRow}>
                <Box
                  sx={{
                    ...s.failedAvatar,
                    backgroundColor: p.accentTint,
                    color: p.accent,
                  }}
                >
                  {index + 1}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box component="span" sx={s.failedName}>
                    {subjectOf(entry)}
                  </Box>
                  <Box component="span" sx={s.failedContact}>
                    {createdOf(entry)
                      ? `Ready ${calculateRelativeTime(createdOf(entry) as string)}`
                      : "Ready for you"}
                  </Box>
                </Box>
                <Button
                  variant="text"
                  onClick={() => openEnvelope(entry)}
                  sx={{ fontSize: t.footerButtonFontSize, fontWeight: 700 }}
                >
                  Open
                </Button>
              </Box>
            ))}
          </>
        )}
      </Box>
    </Box>
  );
};

export default BulkSigningQueue;
